import { Box, Button, ButtonGroup, Card, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography, Icon, IconButton, Avatar } from '@mui/material'; // Import IconButton
import { CloudUpload as CloudUploadIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material'; // Import CloudUploadIcon
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, { useEffect, useState, useRef } from 'react'; // Import useRef
import AptaTable from 'examples/AptaTable';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import axios from 'axios';
import { URL } from 'constants/constant'; // Ensure URL is imported
import Swal from 'sweetalert2';
const OrganisersManagement = () => {
    const [selectedOrganiser, setSelectedOrganiser] = useState({ name: "", image: "", category: "", country: "", type: "" });
    const [open, setOpen] = useState(false);
    const [organisers, setOrganisers] = useState([]);
    const [formType, setFormType] = useState('add');

    const handleOpen = (type, item = { name: "", image: "", category: "", country: "", type: "" }) => {
        setFormType(type);
        setSelectedOrganiser(item);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleSave = async (data) => {
        try {
            const formData = new FormData();
            formData.append("name", data.name);
            formData.append("type", data.type);
            formData.append("country", data.country);
            formData.append("category", data.category);
            if (data.profilePic instanceof File) {
                formData.append("profilePic", data.profilePic);
            }

            if (formType === 'add') {
                await axios.post(`${URL}/api/admin/organization`, formData);
                Swal.fire("Success!", "Organiser added successfully!", "success");
            } else {
                await axios.patch(`${URL}/api/admin/organization/${data._id}`, formData);
                Swal.fire("Success!", "Organiser updated successfully!", "success");
            }

            handleClose();
            getOrganisers();
        } catch (error) {
            console.error("Error saving organiser:", error);
            Swal.fire("Error!", "Failed to save organiser.", "error");
            handleClose();
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will delete the organiser permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${URL}/api/admin/organization/${id}`);
                    Swal.fire("Deleted!", "The organiser has been deleted.", "success");
                    getOrganisers();
                } catch (err) {
                    console.error("Delete error:", err);
                    Swal.fire("Error!", "Failed to delete organiser.", "error");
                }
            }
        });
    };

    const getOrganisers = async () => {
        try {
            const response = await axios.get(`${URL}/api/admin/organization`);
            if (response.data) {
                setOrganisers(response.data.data.reverse());
            }
        } catch (err) {
            console.error("Error fetching organisers:", err);
            Swal.fire("Error!", "Failed to fetch organisers.", "error");
        }
    };

    useEffect(() => {
        getOrganisers();
    }, []);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Box display={'flex'} mb={3} justifyContent={'end'}>
                <Button startIcon={<Icon>add</Icon>} style={{ color: 'white' }} onClick={() => handleOpen('add')} variant='contained' color='primary'>
                    Add Committee
                </Button>
            </Box>
            <AptaTable
                columns={['Name', 'Country', 'Type', 'Category', 'Actions']}
                rows={organisers?.map((item) => ({
                    Name: <MDTypography variant="caption">{item?.name}</MDTypography>,
                    Country: <MDTypography variant="caption">{item?.country}</MDTypography>,
                    Type: <MDTypography variant="caption">{item?.type}</MDTypography>,
                    Category: <MDTypography variant="caption">{item?.category}</MDTypography>,
                    Actions: (
                        <ButtonGroup size='small'>
                            <Button color='info' onClick={() => handleOpen('edit', item)}>
                                <Icon color='info'>edit</Icon>
                            </Button>
                            <Button color="error" onClick={() => handleDelete(item?._id)}>
                                <Icon color='error'>delete</Icon>
                            </Button>
                        </ButtonGroup>
                    )
                }))}
            />
            <InputFormModal
                open={open}
                onClose={handleClose}
                onSave={handleSave}
                details={selectedOrganiser}
                type={formType}
            />
        </DashboardLayout>
    );
};

export default OrganisersManagement;

const InputFormModal = ({ open, onClose, onSave, details, type }) => {
    const [formData, setFormData] = useState({ name: "", profilePic: "", type: "", country: "", category: "" });
    const fileInputRef = useRef(null);
    const typeOptions = [
        "organising",
        "scientific",
        "advisory",
        "preferred paper",
        "posters",
        "scientific exhibits",
        "quiz",
        "workshops",
        "website",
        "audio visual",
        "souvenir",
        "international faculty coordination",
        "venue coordination",
        "trade relations",
        "print & electronic faculty media",
        "finance",
        "registrations",
        "transport",
        "accommodation",
        "catering",
        "tours & travels",
        "cultural",
        "reception",
        "spouse & kids entertainment",
        "central office bearers iria 2025",
        "indian college of radiology & imaging 2025 - 2026",
        "indian journal of radiology imaging 2025-2026",
        "central office bearers iria 2026"
    ];

    useEffect(() => {
        if (details) {
            setFormData(details);
        } else {
            // Reset form when opening for 'add' type or when details are null/undefined
            setFormData({ name: "", profilePic: "", type: "", country: "", category: "" });
        }
    }, [details, open]); // Added 'open' to dependency array to reset when modal opens for 'add'

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, profilePic: file });
        }
    };

    const handleSave = () => {
        onSave(formData);
    };

    const isSaveDisabled = () => {
        // Trim values before checking for emptiness
        return (
            !formData.name?.trim() ||
            !formData.country?.trim() ||
            !formData.type?.trim() ||
            !formData.category?.trim()
        );
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
            <DialogTitle>{type === 'add' ? 'Add Committee' : 'Edit Committee'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
                    <Box sx={{ mt: 3, mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={fileInputRef}
                            onChange={handleImageChange}
                        />
                        <Avatar
                            src={
                                formData.profilePic
                                    ? typeof formData.profilePic === "string"
                                        ? `${URL || ''}/${formData.profilePic}` // Use URL if defined, otherwise empty string
                                        : window.URL.createObjectURL(formData.profilePic)
                                    : null
                            }
                            sx={{
                                width: 120,
                                height: 120,
                                cursor: "pointer",
                                bgcolor: formData.profilePic ? "transparent" : "grey.300",
                                fontSize: 64,
                            }}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {!formData.profilePic && <AccountCircleIcon sx={{ fontSize: 80, color: "grey.600" }} />}
                        </Avatar>
                        <Typography variant="caption" color="textSecondary" sx={{ cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}>
                            Click avatar to upload profile picture
                        </Typography>
                    </Box>

                    <TextField
                        label="Name"
                        name="name"
                        type="text"
                        value={formData.name || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required // Add required prop for visual indication
                        error={!formData.name?.trim() && isSaveDisabled()} // Show error if empty on save attempt
                        helperText={!formData.name?.trim() && isSaveDisabled() ? "Name is required" : ""}
                    />

                    <TextField
                        label="Country"
                        name="country"
                        type="text"
                        value={formData.country || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        error={!formData.country?.trim() && isSaveDisabled()}
                        helperText={!formData.country?.trim() && isSaveDisabled() ? "Country is required" : ""}
                    />

                    <TextField
                        label="Category"
                        name="category"
                        type="text"
                        value={formData.category || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        error={!formData.category?.trim() && isSaveDisabled()}
                        helperText={!formData.category?.trim() && isSaveDisabled() ? "Category is required" : ""}
                    />

                    <FormControl fullWidth margin="normal" required error={!formData.type?.trim() && isSaveDisabled()}>
                        <InputLabel id="type-select-label">Type</InputLabel>
                        <Select
                            labelId="type-select-label"
                            id="type-select"
                            name="type"
                            value={formData.type || ''}
                            label="Type"
                            onChange={handleChange}
                            style={{ minHeight: 40 }}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {typeOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option.charAt(0).toUpperCase() + option.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                        {!formData.type?.trim() && isSaveDisabled() && (
                            <Typography variant="caption" color="error">Type is required</Typography>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="primary">Cancel</Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={isSaveDisabled()}
                    style={{ color: 'white' }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

